import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { emailPrefsAPI } from '@/services/api';
import { CheckCircle2, Loader2, AlertTriangle, MailX } from 'lucide-react';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function Unsubscribe() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | done | error
  const [email, setEmail] = useState('');
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const d = await emailPrefsAPI.getPrefs(token);
        setEmail(d.email || '');
        setUnsubscribed(!!d.unsubscribed);
        setStatus('ready');
      } catch (err) {
        setStatus('error');
        setError(err?.response?.data?.detail || 'This link is no longer valid.');
      }
    })();
  }, [token]);

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await emailPrefsAPI.unsubscribe(token);
      setUnsubscribed(true);
      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  const doResubscribe = async () => {
    setBusy(true);
    try {
      await emailPrefsAPI.resubscribe(token);
      setUnsubscribed(false);
      setStatus('ready');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6" data-testid="unsubscribe-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <div data-testid="unsubscribe-loading">
            <Loader2 className="w-8 h-8 text-slate-400 dark:text-zinc-500 mx-auto mb-4 animate-spin" />
            <p className="text-slate-500 dark:text-zinc-400 text-sm">Loading your preferences…</p>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="unsubscribe-error">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Link not valid</h1>
            <p className="text-red-600 dark:text-red-400 text-sm mb-6">{error}</p>
            <Link to="/login" className="inline-block bg-slate-900 dark:bg-white text-white dark:text-black font-medium px-6 py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors">
              Back to Numuv
            </Link>
          </div>
        )}

        {status === 'ready' && !unsubscribed && (
          <div data-testid="unsubscribe-confirm">
            <MailX className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Unsubscribe from match emails?</h1>
            <p className="text-slate-500 dark:text-zinc-500 text-sm mb-2">
              {email ? <>Emails to <strong>{email}</strong></> : 'These emails'} about new job matches will stop.
            </p>
            <p className="text-slate-400 dark:text-zinc-600 text-xs mb-6">
              You'll still get important account emails (sign-in codes, offer letters, password resets).
            </p>
            {error && <p className="text-red-600 dark:text-red-400 text-sm mb-4">{error}</p>}
            <button onClick={doUnsubscribe} disabled={busy} data-testid="unsubscribe-btn" className="inline-flex items-center gap-2 bg-red-600 text-white font-medium px-6 py-2.5 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Unsubscribe
            </button>
          </div>
        )}

        {(status === 'done' || (status === 'ready' && unsubscribed)) && (
          <div data-testid="unsubscribe-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">You're unsubscribed</h1>
            <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">
              {email ? <><strong>{email}</strong> won't</> : "You won't"} receive job-match emails anymore. Changed your mind?
            </p>
            {error && <p className="text-red-600 dark:text-red-400 text-sm mb-4">{error}</p>}
            <button onClick={doResubscribe} disabled={busy} data-testid="resubscribe-btn" className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-black font-medium px-6 py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Re-subscribe
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
